// client/src/components/bank/PastBids.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatInterestRate, formatCurrencyWithSymbol,formatDuration } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { Files } from 'lucide-react';

const PastBids = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { type } = useParams();
  const location = useLocation();
  const isHotMoney = type === 'hot-money';
  const hasTypeFilter = !!type;
  const [bids, setBids] = useState([]);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState({ excel: false, pdf: false });
 
  // Helper function to determine the rate type
  const getRateType = (bid) => {
    // First check if the bid has a rateType property
    if (bid.rateType) {
      return bid.rateType;
    }
    // Otherwise check if the request has a rateType property
    else if (bid.request?.rateType) {
      return bid.request.rateType;
    }
    // Default to fixed if no rate type is found
    else {
      return 'fixed';
    }
  };
  
  useEffect(() => {
    const fetchBids = async () => {
      try {
        const res = await axios.get('/api/bids/bank');

        // Filter out bids with null requests
        const validBids = res.data.bids.filter(bid => bid.request !== null);

        // Filter by request type when accessed via a type-specific route
        const typedBids = hasTypeFilter
          ? validBids.filter(bid => isHotMoney
              ? bid.request?.requestType === 'hot_money'
              : (!bid.request?.requestType || bid.request?.requestType === 'allocation'))
          : validBids;

        setBids(typedBids);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching bids:');
        setError(t('common.errors.fetchBids'));
        setLoading(false);
      }
    };

    fetchBids();
  }, [t, hasTypeFilter, isHotMoney]);

  // Derived synchronously from bids + activeTab so the table never flashes
  // an intermediate (unfiltered) list while a separate effect catches up.
  const filteredBids = useMemo(() => {
    if (activeTab === 'all') {
      return bids;
    } else if (activeTab === 'pending') {
      return bids.filter(bid => bid.status === 'pending');
    } else if (activeTab === 'completed') {
      return bids.filter(bid =>
        bid.status === 'approved' &&
        bid.allocation?.transferStatus === 'completed'
      );
    } else if (activeTab === 'rejected') {
      return bids.filter(bid => bid.status === 'rejected');
    } else if (activeTab === 'cancelled') {
      return bids.filter(bid => bid.status === 'cancelled');
    }
    return bids;
  }, [activeTab, bids]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleExport = async (format) => {
    if (!filteredBids || filteredBids.length === 0) {
      alert(t('bids.noDataToExport', { defaultValue: 'No data available for export' }));
      return;
    }

    setExportLoading(prev => ({ ...prev, [format]: true }));

    try {
      const response = await axios.get(`/api/bids/export/${format}`, {
        params: {
          status: activeTab !== 'all' ? activeTab : undefined,
          requestType: hasTypeFilter ? (isHotMoney ? 'hot_money' : 'allocation') : undefined
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response header or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Past_Bids_${activeTab}_${Date.now()}.${format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(`${format.toUpperCase()} export error:`, err);
      alert(t('common.error.exportFailed', {
        format: format.toUpperCase(),
        defaultValue: `Failed to export ${format.toUpperCase()} file. Please try again.`
      }));
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };
  
  // Helper function to get the status label/class, including the pending state
  const getStatusInfo = (bid) => {
    if (bid.status === 'pending') return { key: 'pending', label: t('bids.pending') };
    if (bid.status === 'approved') return { key: 'approved', label: t('status.approved') };
    if (bid.status === 'rejected') return { key: 'rejected', label: t('status.rejected') };
    return { key: 'cancelled', label: t('status.cancelled') };
  };

  // Helper function to safely get allocation amount
  const getAllocationAmount = (bid) => {
    // Check for allocation amount in different possible locations
    if (bid.allocation && typeof bid.allocation.amount !== 'undefined') {
      return bid.allocation.amount;
    } else if (typeof bid.allocatedAmount !== 'undefined') {
      return bid.allocatedAmount;
    } else {
      return 0;
    }
  };

  // Mobile Card Component
  const MobilePastBidCard = ({ bid }) => {
    const rateType = getRateType(bid);
    
    return (
      <div className="mobile-record-card">
        <div className="mobile-card-header">
          <div className="mobile-card-title">
            {bid.request?.requestId ? `#${bid.request.requestId}` : t('bids.notAvailable')}
          </div>
          <div className="mobile-card-status">
            <span className={`status status-${getStatusInfo(bid).key}`}>
              {getStatusInfo(bid).label}
            </span>
          </div>
        </div>

        <div className="mobile-card-body">
          {!hasTypeFilter && (
            <div className="mobile-card-row">
              <span className="mobile-card-label">{t('requests.type')}</span>
              <span className="mobile-card-value">
                <span className="status" style={bid.request?.requestType === 'hot_money'
                  ? { backgroundColor: '#f8dfb0', color: '#8a5300' }
                  : { backgroundColor: '#cfe3fb', color: '#0d47a1' }}>
                  {bid.request?.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
                </span>
              </span>
            </div>
          )}

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.company')}</span>
            <span className="mobile-card-value">
              {bid.request?.company?.name || t('bids.notAvailable')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.liquidityAvailable')}</span>
            <span className="mobile-card-value">
              {bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || bid.currency || 'EUR') : t('bids.notAvailable')}
            </span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.bidAmount')}</span>
            <span className="mobile-card-value">{formatCurrencyWithSymbol(bid.amount, bid.currency || bid.request?.currency || 'EUR')}</span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.allocation')}</span>
            <span className="mobile-card-value">
              {bid.status === 'approved' 
                ? formatCurrencyWithSymbol(getAllocationAmount(bid), bid.allocation?.currency || bid.currency || bid.request?.currency || 'EUR')
                : formatCurrencyWithSymbol(0, bid.currency || bid.request?.currency || 'EUR')}
            </span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.rateType')}</span>
            <span className="mobile-card-value">
              {rateType === 'floating' ? t('requests.floating') : t('requests.fixed')}
            </span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('bids.interestRate')}</span>
            <span className="mobile-card-value">{formatInterestRate(bid.interestRate, rateType, bid.baseRate)}</span>
          </div>
          
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.maturity')}</span>
            <span className="mobile-card-value">
              {formatDuration(bid.request?.duration, t)}
            </span>
          </div>
        </div>
        
        <div className="mobile-card-footer">
          <div className="mobile-card-date">
            {new Date(bid.createdAt).toLocaleDateString()}
          </div>
          <div className="mobile-card-action">
            <Link
              to={`/bank/bids/${bid.id}`}
              state={{ from: location.pathname, activeTab }}
              className="view-btn"
            >
              {t('common.view')}
            </Link>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return <div className="loading">{t('common.loadingBids')}</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="past-bids company-dashboard">
      <div className="tab-container">
        <div className="tabs">
          <div
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            {t('bids.allPast')}
          </div>
          <div
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            {t('bids.pending')}
          </div>
          <div
            className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => handleTabChange('completed')}
          >
            {t('status.completedPlural')}
          </div>
          <div
            className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            {t('status.rejectedPlural')}
          </div>
          <div
            className={`tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            {t('status.cancelledPlural')}
          </div>
        </div>
      </div>

      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Files className="card-title-icon" size={20} />
            <span>
              {hasTypeFilter
                ? `${t('bids.pastBids')} - ${isHotMoney ? t('requests.hotMoney') : t('requests.allocation')}`
                : t('bids.pastBids')}
            </span>
          </div>

          {/* Export Actions */}
          {filteredBids.length > 0 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleExport('excel')}
                disabled={exportLoading.excel}
                className="btn btn-primary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.excel ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportExcel', { defaultValue: 'Export Excel' })}
                  </>
                )}
              </button>
              <button
                onClick={() => handleExport('pdf')}
                disabled={exportLoading.pdf}
                className="btn btn-secondary"
                style={{
                  fontSize: '12px',
                  padding: '2px 14px !important',
                  height: '22px !important',
                  minHeight: '22px !important',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px'
                }}
              >
                {exportLoading.pdf ? (
                  <>
                    <div className="spinner" style={{ width: '10px', height: '10px' }}></div>
                    {t('common.exporting', { defaultValue: 'Exporting...' })}
                  </>
                ) : (
                  <>
                    {t('common.exportPdf', { defaultValue: 'Export PDF' })}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
        
        {/* Desktop Table */}
        {!isMobile && (
          <table>
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.company')}</th>
                <th>{t('bids.liquidityAvailable')}</th>
                <th>{t('bids.bidAmount')}</th>
                <th>{t('bids.allocation')}</th>
                <th>{t('bids.interestRate')}</th>
                <th>{t('requests.maturity')}</th>
                <th>{t('bids.submissionDate')}</th>
                {!hasTypeFilter && <th>{t('requests.type')}</th>}
                <th>{t('bids.status')}</th>
                <th>{t('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredBids.length > 0 ? (
                filteredBids.map(bid => {
                  // Get the rate type for this bid
                  const rateType = getRateType(bid);
                  
                  return (
                    <tr key={bid.id}>
                      <td>{bid.request?.requestId ? `#${bid.request.requestId}` : t('bids.notAvailable')}</td>
                      <td>
                        {bid.request?.company?.name || t('bids.notAvailable')}
                      </td>
                      <td>{bid.request?.amount ? formatCurrencyWithSymbol(bid.request.amount, bid.request?.currency || bid.currency || 'EUR') : t('bids.notAvailable')}</td>
                      <td>{formatCurrencyWithSymbol(bid.amount, bid.currency || bid.request?.currency || 'EUR')}</td>
                      <td>
                        {bid.status === 'approved' 
                          ? formatCurrencyWithSymbol(getAllocationAmount(bid), bid.allocation?.currency || bid.currency || bid.request?.currency || 'EUR')
                          : formatCurrencyWithSymbol(0, bid.currency || bid.request?.currency || 'EUR')}
                      </td>
                      <td>{formatInterestRate(bid.interestRate, rateType, bid.baseRate)}</td>
                      <td>
                        {formatDuration(bid.request?.duration, t)}
                      </td>
                      <td style={{ fontSize: '0.9em' }}>{new Date(bid.createdAt).toLocaleDateString()}</td>
                      {!hasTypeFilter && (
                        <td>
                          <span className="status" style={bid.request?.requestType === 'hot_money'
                            ? { backgroundColor: '#f8dfb0', color: '#8a5300', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }
                            : { backgroundColor: '#cfe3fb', color: '#0d47a1', fontSize: '10px', padding: '3px 7px', lineHeight: '1.4' }}>
                            {bid.request?.requestType === 'hot_money' ? t('requests.hotMoney') : t('requests.allocation')}
                          </span>
                        </td>
                      )}
                      <td>
                        <span
                          className={`status status-${getStatusInfo(bid).key}`}
                          style={{ fontSize: '10px', padding: '3px 7px', lineHeight: '1.4', border: '1px solid transparent' }}
                        >
                          {getStatusInfo(bid).label}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/bank/bids/${bid.id}`}
                          state={{ from: location.pathname, activeTab }}
                          className="view-btn"
                          style={{ paddingLeft: '8.2px', paddingRight: '8.2px', fontSize: '13px' }}
                          >
                            {t('common.view')}
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={hasTypeFilter ? 10 : 11}>{t('bids.noPastBidsFound')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        
        {/* Mobile Cards - Solo su mobile */}
        {isMobile && (
          <div className="mobile-cards-container">
            {filteredBids.length > 0 ? (
              filteredBids.map(bid => (
                <MobilePastBidCard key={bid.id} bid={bid} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('bids.noPastBidsFound')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastBids;